import axios from 'axios';
import invoke from 'lodash/invoke';
import noop from 'lodash/noop';
import {inject} from 'mobx-react';
import PropTypes from 'prop-types';
import React, {Component} from 'react';
import Dropzone from 'react-dropzone';

//helpers
import {uploadFileToCloudinary} from '../../utils/cloudinary-utils';
import {convertBToMb, getResourceType, getUploadPresetForFile, isImageUrl, resourceAllowed} from 'utils/media-utils';

//components
import {FormattedMessage} from 'components/FormattedComponents';

//styles
import {
  AddPictureIcon,
  CancelButton,
  CircularProgressBar,
  ControlButton,
  ControlButtonIcon,
  ControlsWrapper,
  MediaUploader,
  TextWrapper
} from './styles';

//messages
import messages from './messages';

//constants
import {ALLOW_VIDEOS, ALLOWED_FILE_TYPES, READY_STATE} from './constants';

// This component always capped every upload at 20MB; only videos moved to the platform
// limit. Whether non-video uploads deserve a configurable limit too is an open decision.
const NON_VIDEO_UPLOAD_LIMIT_MB = 20;

@inject('store')
class MediaUploaderComponent extends Component {
  static defaultProps = {
    onUploadStart: noop,
    onUploadEnd: noop,
    onBeforeRemove: () => Promise.resolve(),
    onChange: noop,
    onError: noop
  };

  static propTypes = {
    dropzoneRef: PropTypes.func,
    onUploadStart: PropTypes.func, // when the uploading starts
    onUploadEnd: PropTypes.func, // when the uploading ends
    onBeforeRemove: PropTypes.func, // if the result rejects, won't remove
    onChange: PropTypes.func, // when the uploading finished and the new value is passed
    onError: PropTypes.func,
    value: PropTypes.string,
    isDesktop: PropTypes.bool,
    store: PropTypes.object
  };

  axiosRequestToken = null;
  isUnmounted = false;
  lastAcceptedFile = null;

  state = {
    isUploading: false,
    progress: 0
  };

  get readyState() {
    const {value} = this.props;
    const {isUploading} = this.state;

    if (isUploading) {
      return READY_STATE.LOADING;
    }

    if (value) {
      return READY_STATE.DONE;
    }

    return READY_STATE.EMPTY;
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value) {
      this.cancelPendingRequest();
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    this.cancelPendingRequest();
  }

  cancelPendingRequest = () => {
    invoke(this.axiosRequestToken, 'cancel');
  };

  handleDropAccepted = ([file]) => {
    const {
      onUploadStart,
      onError,
      store: {
        platform: {maxVideoUploadMb}
      }
    } = this.props;
    if (getResourceType(file) !== 'video' && convertBToMb(file.size) >= NON_VIDEO_UPLOAD_LIMIT_MB) {
      onError({type: 'tooLarge', data: {allowedSize: NON_VIDEO_UPLOAD_LIMIT_MB}});
      return;
    }

    const {allowed, error} = resourceAllowed(file, {
      allowVideos: ALLOW_VIDEOS,
      allowedFileTypes: ALLOWED_FILE_TYPES,
      videoLimitMb: maxVideoUploadMb
    });

    if (allowed) {
      this.lastAcceptedFile = file;

      onUploadStart();

      this.upload(file)
        .catch(() => onError({type: 'networkError'}))
        .finally(() => {
          const {onUploadEnd} = this.props;
          onUploadEnd();
        });
    } else {
      onError(error);
    }
  };

  handleDropzoneClick = event => {
    // Allow opening the file dialog by clicking the entire control only
    // when no image is selected:
    if (this.readyState !== READY_STATE.EMPTY) {
      event.preventDefault();
    }
  };

  handleResetClick = event => {
    const {onBeforeRemove} = this.props;

    onBeforeRemove().then(
      () => this.props.onChange(undefined),
      () => {
        /* catch, but do nothing */
      }
    );

    event.stopPropagation();
  };

  handleUploadProgress = event => {
    this.setState({progress: event.loaded / event.total});
  };

  retry = () => {
    if (this.lastAcceptedFile) {
      this.handleDropAccepted([this.lastAcceptedFile]);
    }
  };

  upload = async file => {
    try {
      const {store} = this.props;
      const {platform} = store;

      if (!platform.assetsFolder) {
        throw new Error('Missing platform.assetsFolder');
      }

      this.setState({isUploading: true, progress: 0});

      this.axiosRequestToken = axios.CancelToken.source();

      // Viewer images stay on the unsigned preset; only the CMS picker uses the resize preset.
      const uploadPreset =
        getResourceType(file) === 'video'
          ? getUploadPresetForFile(file, {optimizedVideos: platform.hasOptimizedVideos})
          : 'unsigned';

      const data = await uploadFileToCloudinary(file, {
        folder: platform.assetsFolder,
        uploadPreset,
        cancelToken: this.axiosRequestToken,
        onUploadProgress: this.handleUploadProgress
      });

      const {onChange} = this.props;
      onChange(data.secure_url);
    } catch (error) {
      if (!error || axios.isCancel(error)) {
        return;
      }
      throw error;
    } finally {
      if (!this.isUnmounted) {
        this.setState({isUploading: false});
      }
    }
  };

  render() {
    const {dropzoneRef, value, isDesktop} = this.props;
    const {progress} = this.state;
    const isImage = isImageUrl(value);

    return (
      <Dropzone
        accept={ALLOWED_FILE_TYPES}
        disabled={this.readyState === READY_STATE.LOADING}
        multiple={false}
        onClick={this.handleDropzoneClick}
        onDropAccepted={this.handleDropAccepted}
        ref={dropzoneRef}
      >
        {({getRootProps, getInputProps, open}) => (
          <MediaUploader
            isImage={!value || isImage}
            backgroundURL={isImage ? value : undefined}
            isDesktop={isDesktop}
            readyState={this.readyState}
            {...getRootProps({refKey: 'innerRef'})}
          >
            {value && !isImage && (
              <video width={'100%'} height={'100%'} controls preload="metadata">
                <source src={`${value}#t=0.1`} />
              </video>
            )}
            <input {...getInputProps()} />
            {this.readyState === READY_STATE.EMPTY ? (
              <TextWrapper>
                <AddPictureIcon />
                <FormattedMessage {...messages.selectFile} />
              </TextWrapper>
            ) : this.readyState === READY_STATE.LOADING ? (
              <TextWrapper>
                <CircularProgressBar value={progress} />
                <FormattedMessage {...messages.uploadingFile} />
                <CancelButton onClick={this.cancelPendingRequest}>
                  <FormattedMessage {...messages.cancel} />
                </CancelButton>
              </TextWrapper>
            ) : (
              <ControlsWrapper isImage={!value || isImage}>
                <ControlButton onClick={open}>
                  <ControlButtonIcon name="image-search" />
                </ControlButton>
                <ControlButton onClick={this.handleResetClick} data-testid="ResetButton">
                  <ControlButtonIcon name="trashbin" />
                </ControlButton>
              </ControlsWrapper>
            )}
          </MediaUploader>
        )}
      </Dropzone>
    );
  }
}

export default MediaUploaderComponent;
